// TrainerService — migrated to the OpenAPI-generated client (AdminTrainerController /admin/trainer).
// Each function keeps its old signature + return shape (callers read res.data / res.data.content), so
// the adapter returns `{data}` (data = the parsed body) and re-throws an axios-shaped error on failure.
// The auth/timezone headers are injected by configureApi's interceptor, so no token is threaded here.
//
// NOTE: the server derives the caller's company from the principal (IDOR sweep); the `companyId`
// argument these functions still accept is intentionally NOT forwarded to the SDK — it is dropped,
// matching the server contract where /admin/trainer has no companyId param.

import {
  adminTrainerControllerGet,
  adminTrainerControllerMakeDefaultTrainer,
  adminTrainerControllerUpdate,
  adminTrainerControllerCreate,
  adminTrainerControllerMakeUserTrainer,
  adminTrainerControllerEnableTrainer,
  adminTrainerControllerDisableTrainer,
  adminTrainerControllerDelete,
  adminTrainerControllerGetAll,
} from '../api/generated';

// Preserve the legacy axios rejection shape so call sites reading `e.response.data.errorCode` keep working.
const throwLikeAxios = (error, response) => {
  throw {response: {data: error, status: response?.status}};
};

export const getTrainer = async (id) => {
  const {data, error, response} = await adminTrainerControllerGet({path: {id}});
  if (error) throwLikeAxios(error, response);
  return {data};
};

export const makeDefaultTrainer = async (id) => {
  const {data, error, response} = await adminTrainerControllerMakeDefaultTrainer({path: {id}});
  if (error) throwLikeAxios(error, response);
  return {data};
};

export const updateTrainer = async (updateRequest) => {
  const {data, error, response} = await adminTrainerControllerUpdate({body: updateRequest});
  if (error) throwLikeAxios(error, response);
  return {data};
};

export const createTrainer = async (createRequest) => {
  const {data, error, response} = await adminTrainerControllerCreate({body: createRequest});
  if (error) throwLikeAxios(error, response);
  return {data};
};

export const promotToTrainer = async (userId) => {
  const {data, error, response} = await adminTrainerControllerMakeUserTrainer({path: {userId}});
  if (error) throwLikeAxios(error, response);
  return {data};
};

export const enableTrainer = async (id) => {
  const {data, error, response} = await adminTrainerControllerEnableTrainer({path: {id}});
  if (error) throwLikeAxios(error, response);
  return {data};
};

export const disableTrainer = async (id) => {
  const {data, error, response} = await adminTrainerControllerDisableTrainer({path: {id}});
  if (error) throwLikeAxios(error, response);
  return {data};
};

export const deleteTrainer = async (id) => {
  const {data, error, response} = await adminTrainerControllerDelete({path: {id}});
  if (error) throwLikeAxios(error, response);
  return {data};
};

// The server endpoint returns a Spring Page<T> ({ content, totalElements, ... }). getTrainers keeps its
// historical contract of resolving to a response whose `.data` is the full trainer ARRAY, so the many
// dropdown/picker callers stay unchanged. It requests a single large page to get every trainer.
const FULL_PAGE_SIZE = 10000;

export async function getTrainers(companyId, sortBy, sortDirection) {
  const res = await getTrainersPage(companyId, sortBy, sortDirection, 0, FULL_PAGE_SIZE);
  // Unwrap the page into an array so callers reading res.data keep getting a list.
  return {...res, data: res.data?.content ?? []};
}

// Server-side paged variant. Resolves to a response whose `res.data` is the Spring Page object
// ({ content, totalElements, totalPages, number, ... }). Optional filters (keyword/gender/state) are
// applied server-side BEFORE paging. The endpoint uses FLAT pageId/pageSize query params (not a nested
// Spring Pageable), so it maps cleanly to the SDK query. companyId is dropped (see note at top).
export async function getTrainersPage(companyId, sortBy, sortDirection, pageId = 0, pageSize = 25, filters = {}) {
  const {keyword, gender, state} = filters;

  const query = {pageId, pageSize};
  if (sortBy) query.sortBy = sortBy;
  if (sortDirection) query.sortDirection = sortDirection;
  if (keyword) query.keyword = keyword;
  if (gender) query.gender = gender;
  if (state !== undefined && state !== null) query.state = state;

  const {data, error, response} = await adminTrainerControllerGetAll({query});
  if (error) throwLikeAxios(error, response);
  return {data};
}
